import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { FormGroup } from '@/components/ui/form'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import NumberField from 'app/frontend/base/components/form-fields/NumberField'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import { usePageInfo } from 'app/frontend/base/config/queries'
import raidbotsFightStyles from 'app/frontend/types/custom/RaidbotsFightStyle'
import { Trash2, Undo2 } from 'lucide-react'
import { useFieldArray, useFormContext } from 'react-hook-form'

export type EditableDroptimizerConfiguration = Partial<
  wowaudit.DroptimizerConfiguration & {
    _destroy?: boolean
  }
>

const operatorOptions = {
  'is less than': 'smaller',
  'is at most': 'smaller_or_equal',
  is: 'equal',
  'is at least': 'greater_or_equal',
  'is more than': 'greater',
}

const name = 'droptimizer_configurations_attributes'

const DroptimizerConfigurationFormContent = () => {
  const {
    data: {
      metadata: { droptimizerUpgradeLevels },
    },
  } = usePageInfo()
  const { watch, setValue } = useFormContext()
  const { append, remove } = useFieldArray({ name })

  const droptimizerConfigurations: EditableDroptimizerConfiguration[] =
    watch(name)

  return (
    <div>
      <div className='flex flex-col gap-2'>
        {droptimizerConfigurations.filter((c) => !c._destroy).length === 0 && (
          <Alert>
            <AlertTitle>You removed all of your configurations.</AlertTitle>
            <AlertDescription>
              Upon saving, a default configuration will be created again
              automatically.
            </AlertDescription>
          </Alert>
        )}

        {droptimizerConfigurations.map((configuration, index) => (
          <div
            className='bg-background py-2 px-3 rounded-md relative border border-input'
            key={`${configuration.id}-${index}`}
          >
            <FormGroup
              className={`${configuration._destroy ? 'opacity-30' : ''}`}
            >
              <TextField
                name={`${name}.${index}.name`}
                inputClassName='bg-muted'
                customLabel='Name'
                customPlaceholder='Name'
              />
              <SelectField
                name={`${name}.${index}.fightStyle`}
                inputClassName='bg-muted hover:bg-muted'
                options={Object.entries(raidbotsFightStyles)}
                labelFn={(option) => option[0]}
                disabled={configuration._destroy}
                valueKey={1}
                customLabel={'Fight style'}
              />
              <NumberField
                layout='inline-shrinkable'
                name={`${name}.${index}.weight`}
                className='w-20 bg-muted'
                customLabel='Weight'
                customPlaceholder='0'
                customDescription=''
              />
            </FormGroup>

            <FormGroup
              className={`[&>span]:font-semibold leading-tight ${
                configuration._destroy ? 'opacity-30' : ''
              }`}
            >
              <span>Boss amount</span>
              <SelectField
                name={`${name}.${index}.numberOfBossesOperator`}
                inputClassName='bg-muted hover:bg-muted'
                options={Object.entries(operatorOptions)}
                labelFn={(option) => option[0]}
                disabled={configuration._destroy}
                valueKey={1}
                customLabel=''
              />
              <NumberField
                layout='inline-shrinkable'
                name={`${name}.${index}.numberOfBosses`}
                className='w-16 bg-muted'
                customLabel=''
                customPlaceholder='0'
                customDescription=''
              />

              <span className='ml-4'>Fight length</span>
              <SelectField
                name={`${name}.${index}.fightDurationOperator`}
                inputClassName='bg-muted hover:bg-muted'
                options={Object.entries(operatorOptions)}
                disabled={configuration._destroy}
                labelFn={(option) => option[0]}
                valueKey={1}
                customLabel=''
              />
              <NumberField
                layout='inline-shrinkable'
                name={`${name}.${index}.fightDuration`}
                className='w-16 bg-muted'
                customLabel=''
                customPlaceholder='0'
                customDescription=''
              />
              <span className='mt-[0.6rem]'>min</span>
            </FormGroup>

            <FormGroup
              className={`gap-6 ${configuration._destroy ? 'opacity-30' : ''}`}
            >
              <CheckboxField
                layout='inline'
                name={`${name}.${index}.sockets`}
                customLabel='Require gems'
                customDescription=''
              />
              <CheckboxField
                layout='inline'
                name={`${name}.${index}.pi`}
                customLabel='Require PI'
                customDescription=''
              />
              <CheckboxField
                layout='inline'
                name={`${name}.${index}.expertMode`}
                customLabel='Allow expert mode & custom APL'
                customDescription=''
              />
            </FormGroup>

            <label
              className={`font-semibold mb-1 block ${
                configuration._destroy ? 'opacity-30' : ''
              }`}
            >
              Upgrade up to
            </label>
            <FormGroup>
              {['', 'Heroic', 'Normal', 'Lfr'].map((difficulty, i) => (
                <SelectField
                  key={difficulty}
                  name={`${name}.${index}.upgradeLevel${
                    difficulty.length ? difficulty : ''
                  }`}
                  inputClassName='bg-muted hover:bg-muted'
                  options={Object.values(droptimizerUpgradeLevels)[i]}
                  className={`${configuration._destroy ? 'opacity-30' : ''}`}
                  disabled={configuration._destroy}
                  transformOptions={false}
                  customLabel={
                    {
                      '': 'Mythic',
                      Heroic: 'Heroic',
                      Normal: 'Normal',
                      Lfr: 'LFR',
                    }[difficulty]
                  }
                />
              ))}
            </FormGroup>

            <FormGroup className='justify-between'>
              <div className={`${configuration._destroy ? 'opacity-30' : ''}`}>
                <CheckboxField
                  layout='inline'
                  name={`${name}.${index}.matchEquippedGear`}
                  customLabel="Require 'Upgrade All Equipped Gear to the Same Level'"
                  customDescription=''
                />
              </div>

              <Button
                type='button'
                size='sm'
                variant='outline'
                onClick={() =>
                  configuration.id
                    ? setValue(
                        `${name}.${index}._destroy`,
                        !configuration._destroy,
                      )
                    : remove(index)
                }
              >
                {configuration._destroy ? (
                  <Undo2 size={14} />
                ) : (
                  <Trash2 size={14} />
                )}
              </Button>
            </FormGroup>
          </div>
        ))}
      </div>

      <div className='flex justify-end'>
        <Button
          type='button'
          size='sm'
          className='mt-2'
          variant='outline'
          onClick={() =>
            append({
              name: 'New configuration',
              fightStyle: 'Patchwerk',
              sockets: false,
              pi: false,
              matchEquippedGear: false,
              numberOfBossesOperator: 'equal',
              numberOfBosses: 1,
              fightDurationOperator: 'greater_or_equal',
              fightDuration: 5,
              upgradeLevel: 0,
              upgradeLevelHeroic: 0,
              upgradeLevelNormal: 0,
              upgradeLevelLfr: 0,
              weight: 1,
              expertMode: false,
            })
          }
        >
          Add new configuration
        </Button>
      </div>
    </div>
  )
}

export default DroptimizerConfigurationFormContent
